import { opportunityFilterDefinitions } from "@/app/[tenant]/(console)/growth/leads/opportunity-filter-definitions";

import {
  CampaignDeliveryDateRange,
  CampaignMetricsResponse,
  CampaignPerformanceSummary,
  DashboardKPIs,
  LostReasonCreate,
  LostReasonSummary,
  OpportunityAssignmentUpdate,
  OpportunityBulkActionResult,
  OpportunityBulkArchive,
  OpportunityBulkCampaignUpdate,
  OpportunityBulkCreate,
  OpportunityBulkCreateResponse,
  OpportunityBulkMarketingConsent,
  OpportunityBulkStatusUpdate,
  OpportunityContactLogCreate,
  OpportunityCreate,
  OpportunityDetail,
  OpportunityEnrollmentUpdate,
  OpportunityFetchParams,
  OpportunityGroupBy,
  OpportunityGroupItem,
  OpportunityLogDetail,
  OpportunityNoteCreate,
  OpportunityNoteSummary,
  OpportunityStageUpdate,
  OpportunityStatusUpdate,
  OpportunitySummary,
  OpportunityUpdate,
} from "@/types/opportunity";
import { Page } from "@/types/pagination";

import { requestData } from "@/api/api-config";
import { serializeFilterQuerySegments } from "@/features/table-filters/contract";

export async function fetchOpportunities(
  params?: OpportunityFetchParams,
): Promise<Page<OpportunitySummary> | null> {
  const searchParams = params
    ? serializeFilterQuerySegments([
        { definitions: opportunityFilterDefinitions, values: params },
      ])
    : undefined;

  return await requestData<Page<OpportunitySummary>>(
    "opportunity/list",
    "get",
    { searchParams },
  );
}

export async function fetchOpportunityGroups(
  groupBy: OpportunityGroupBy,
  params?: OpportunityFetchParams,
): Promise<OpportunityGroupItem[]> {
  const searchParams = serializeFilterQuerySegments([
    { definitions: opportunityFilterDefinitions, values: params ?? {} },
  ]);
  searchParams.set("group_by", groupBy);

  return (
    (await requestData<OpportunityGroupItem[]>("opportunity/groups", "get", {
      searchParams,
    })) ?? []
  );
}

export async function fetchOpportunity(
  opportunityId: string,
): Promise<OpportunityDetail | null> {
  return await requestData<OpportunityDetail>(
    `opportunity/${opportunityId}`,
    "get",
  );
}

export async function createOpportunity(
  opportunity: OpportunityCreate,
): Promise<OpportunitySummary | null> {
  // catchError: false so HTTP errors throw and surface in mutation onError
  // (with requestData's default catch, failures resolve as null → false success).
  return await requestData<OpportunitySummary>(
    "opportunity",
    "post",
    { json: opportunity },
    false,
  );
}

export async function createOpportunitiesBulk(
  data: OpportunityBulkCreate,
): Promise<OpportunityBulkCreateResponse> {
  const result = await requestData<OpportunityBulkCreateResponse>(
    "opportunity/bulk",
    "post",
    { json: data },
    false,
  );
  if (!result) {
    throw new Error("Bulk import did not return a response. Please try again.");
  }
  return result;
}

export async function updateOpportunity(
  opportunityId: string,
  opportunity: OpportunityUpdate,
): Promise<OpportunitySummary | null> {
  return await requestData<OpportunitySummary>(
    `opportunity/${opportunityId}`,
    "put",
    {
      json: opportunity,
    },
    // Throw rather than resolve to null: this write is applied optimistically,
    // so a swallowed failure would leave the change on screen until the next
    // refetch quietly undid it, with no error shown.
    false,
  );
}

export async function deleteOpportunity(opportunityId: string): Promise<void> {
  await requestData(`opportunity/${opportunityId}`, "delete");
}

export async function winOpportunity(
  opportunityId: string,
): Promise<OpportunityDetail | null> {
  return await requestData<OpportunityDetail>(
    `opportunity/${opportunityId}/win`,
    "post",
  );
}

export async function moveOpportunityStage(
  opportunityId: string,
  update: OpportunityStageUpdate,
): Promise<OpportunityDetail | null> {
  return await requestData<OpportunityDetail>(
    `opportunity/${opportunityId}/stage`,
    "put",
    { json: update },
    false,
  );
}

export async function enrollOpportunityInPipeline(
  opportunityId: string,
  update: OpportunityEnrollmentUpdate,
): Promise<OpportunityDetail | null> {
  return await requestData<OpportunityDetail>(
    `opportunity/${opportunityId}/pipeline`,
    "put",
    { json: update },
    false,
  );
}

export async function setOpportunityStatus(
  opportunityId: string,
  update: OpportunityStatusUpdate,
): Promise<OpportunityDetail | null> {
  return await requestData<OpportunityDetail>(
    `opportunity/${opportunityId}/status`,
    "put",
    { json: update },
    false,
  );
}

export async function assignOpportunity(
  opportunityId: string,
  update: OpportunityAssignmentUpdate,
): Promise<OpportunityDetail | null> {
  return await requestData<OpportunityDetail>(
    `opportunity/${opportunityId}/assignment`,
    "put",
    { json: update },
    false,
  );
}

export async function fetchLostReasons(): Promise<LostReasonSummary[]> {
  return (
    (await requestData<LostReasonSummary[]>(
      "opportunity/lost-reasons",
      "get",
    )) ?? []
  );
}

export async function createLostReason(
  reason: LostReasonCreate,
): Promise<LostReasonSummary> {
  const created = await requestData<LostReasonSummary>(
    "opportunity/lost-reasons",
    "post",
    { json: reason },
    false,
  );
  if (!created) throw new Error("Could not add that reason. Please try again.");
  return created;
}

// ---------------------------------------------------------------------------
// Bulk actions
//
// Each returns a per-opportunity tally rather than throwing on the first bad
// row, so the caller can tell the operator exactly what did and did not land.
// ---------------------------------------------------------------------------

async function requestBulkAction(
  path: string,
  json: object,
): Promise<OpportunityBulkActionResult> {
  const result = await requestData<OpportunityBulkActionResult>(
    path,
    "post",
    { json },
    false,
  );
  if (!result) throw new Error("Bulk action did not return a result.");
  return result;
}

export async function bulkSetOpportunityStatus(
  update: OpportunityBulkStatusUpdate,
): Promise<OpportunityBulkActionResult> {
  return await requestBulkAction("opportunity/bulk-status", update);
}

export async function bulkArchiveOpportunities(
  update: OpportunityBulkArchive,
): Promise<OpportunityBulkActionResult> {
  return await requestBulkAction("opportunity/bulk-archive", {
    opportunityIds: update.opportunityIds,
    isArchived: update.isArchived ?? true,
  });
}

export async function bulkSetOpportunityMarketingConsent(
  update: OpportunityBulkMarketingConsent,
): Promise<OpportunityBulkActionResult> {
  return await requestBulkAction("opportunity/bulk-marketing-consent", update);
}

export async function bulkSetOpportunityCampaign(
  update: OpportunityBulkCampaignUpdate,
): Promise<OpportunityBulkActionResult> {
  return await requestBulkAction("opportunity/bulk-campaign", update);
}

export async function fetchOpportunityActivity(
  opportunityId: string,
): Promise<OpportunityLogDetail[] | null> {
  const page = await requestData<Page<OpportunityLogDetail>>(
    `opportunity/${opportunityId}/activity`,
    "get",
  );
  return page?.data ?? null;
}

export async function logOpportunityContact(
  opportunityId: string,
  contact: OpportunityContactLogCreate,
): Promise<OpportunityLogDetail | null> {
  return await requestData<OpportunityLogDetail>(
    `opportunity/${opportunityId}/contact-log`,
    "post",
    { json: contact },
    false,
  );
}

export async function addOpportunityNote(
  opportunityId: string,
  note: OpportunityNoteCreate,
): Promise<OpportunityNoteSummary | null> {
  return await requestData<OpportunityNoteSummary>(
    `opportunity/${opportunityId}/note`,
    "post",
    {
      json: note,
    },
  );
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export async function fetchDashboardKPIs(
  days: number = 30,
): Promise<DashboardKPIs | null> {
  return await requestData<DashboardKPIs>(
    "opportunity/analytics/dashboard",
    "get",
    {
      searchParams: { days },
    },
  );
}

export async function fetchCampaignMetrics(): Promise<CampaignMetricsResponse | null> {
  return await requestData<CampaignMetricsResponse>(
    "opportunity/analytics/campaign-metrics",
    "get",
  );
}

export async function syncCampaignDelivery(
  campaignId: string,
  dateRange: CampaignDeliveryDateRange,
): Promise<CampaignPerformanceSummary> {
  const result = await requestData<CampaignPerformanceSummary>(
    `opportunity/analytics/campaign/${campaignId}/delivery/sync`,
    "post",
    { searchParams: dateRange },
    false,
  );
  if (result === null)
    throw new Error("Provider delivery sync returned no data");
  return result;
}

export async function fetchCampaignPerformance(
  campaignId: string,
  dateRange: CampaignDeliveryDateRange,
): Promise<CampaignPerformanceSummary | null> {
  return await requestData<CampaignPerformanceSummary>(
    `opportunity/analytics/campaign/${campaignId}/performance`,
    "get",
    { searchParams: dateRange },
  );
}

// ---------------------------------------------------------------------------
// AI Summary
// ---------------------------------------------------------------------------

export async function generateOpportunityAISummary(
  opportunityId: string,
): Promise<{ id: string; summary: string; suggestions: string[] } | null> {
  return await requestData(`opportunity/${opportunityId}/ai-summary`, "post");
}
